import { Embed as YoutubeEmbed } from '@vimily/bonjoro-ui-components-v2/Youtube';
import React, { MutableRefObject, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { SocialType } from '~/App/Modules/Socials/util';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { UserWebAssetContent } from '~/types/models';
import { EmbedTile, mapTwitterProps } from '@vimily/bonjoro-ui-components-v2/EmbedTile';
import useDate from '~/react-hooks/use-date';

const hideClassName = 'opacity-0 !w-0 !h-0';

const EmbedLoadingState = ({ loading }: { loading: boolean }) => {
  return (
    <div
      className={cx(
        'rounded v2-bg-neutral-200 flex items-center justify-center transition-opacity',
        {
          [`${hideClassName} hidden`]: !loading,
          'opacity-100 w-full h-40': loading,
        }
      )}
    >
      <FontAwesomeIcon icon={faSpinner} className='animate-spin mr-2' />
    </div>
  );
};

export interface WebAssetEmbedProps {
  url: string;
  type: SocialType;
  content?: UserWebAssetContent;
  className?: string;
  errorStateClassName?: string;
  onLoad?: () => void;
  onLoadError?: () => void;
}

export function WebAssetEmbed({
  url,
  type,
  content,
  className,
  errorStateClassName,
  onLoad,
  onLoadError,
}: WebAssetEmbedProps) {
  const ref = useRef<HTMLIFrameElement | HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const { utc } = useDate(content?.datetime);

  const onEmbedLoad = () => {
    onLoad?.();
    setLoading(false);
  };

  const onEmbedLoadError = () => {
    onLoadError?.();
    setLoading(false);
  };

  const embedComponent = useMemo(() => {
    switch (type) {
      case SocialType.Twitter:
        return (
          <EmbedTile
            ref={ref}
            {...mapTwitterProps({ url, content, datetime: utc })}
            onLoad={onEmbedLoad}
          />
        );
      case SocialType.Youtube:
        return (
          <YoutubeEmbed
            containerRef={ref as MutableRefObject<HTMLIFrameElement>}
            youtubeUrl={url}
            className={cx(className, '!w-full border-none')}
            errorStateClassName={cx(errorStateClassName, 'w-full')}
            onLoad={onEmbedLoad}
            onLoadError={onEmbedLoadError}
          />
        );
    }
  }, [type, url]);

  return (
    <>
      <EmbedLoadingState loading={loading} />
      <div
        className={cx(
          ['flex rounded items-center', 'justify-center transition-opacity'],
          {
            'opacity-100 !w-full min-h-40': !loading,
            [hideClassName]: loading,
          }
        )}
      >
        {embedComponent}
      </div>
    </>
  );
}
